import { Controller } from "@hotwired/stimulus"

const MAX_FILES = 10
const MAX_SIZE = 15 * 1024 * 1024

// Pièces jointes : clic, glisser-déposer et collage (⌘V) de captures d'écran.
// Les fichiers sont conservés dans l'<input type=file> via un DataTransfer.
export default class extends Controller {
  static targets = ["input", "previews", "area"]
  static values = { paste: Boolean }

  connect() {
    this.files = new DataTransfer()
    this.onPaste = this.paste.bind(this)
    if (this.pasteValue) document.addEventListener("paste", this.onPaste)

    this.dragDepth = 0
    this.onDragEnter = (e) => { if (this.hasFiles(e)) { this.dragDepth++; this.element.classList.add("is-dragging") } }
    this.onDragLeave = () => { if (--this.dragDepth <= 0) this.reset() }
    this.onDragOver = (e) => { if (this.hasFiles(e)) e.preventDefault() }
    this.onDrop = (e) => {
      if (!this.hasFiles(e)) return
      e.preventDefault()
      this.reset()
      this.add(e.dataTransfer.files)
    }
    for (const [name, fn] of [["dragenter", this.onDragEnter], ["dragleave", this.onDragLeave], ["dragover", this.onDragOver], ["drop", this.onDrop]]) {
      this.element.addEventListener(name, fn)
    }
    this.inputTarget.addEventListener("change", () => {
      const picked = Array.from(this.inputTarget.files)
      this.inputTarget.files = this.files.files
      this.add(picked)
    })
  }

  disconnect() {
    document.removeEventListener("paste", this.onPaste)
  }

  hasFiles(event) {
    return Array.from(event.dataTransfer?.types || []).includes("Files")
  }

  reset() {
    this.dragDepth = 0
    this.element.classList.remove("is-dragging")
  }

  paste(event) {
    const files = Array.from(event.clipboardData?.files || [])
    if (files.length === 0) return
    // Collage de texte riche (Word, page web…) dans un champ : on laisse le texte passer
    const typing = event.target.closest?.("input, textarea, [contenteditable]")
    if (typing && Array.from(event.clipboardData.types).includes("text/plain")) return
    event.preventDefault()
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")
    this.add(files.map((file, i) => {
      if (file.name && file.name !== "image.png") return file
      const ext = (file.type.split("/")[1] || "png").replace("jpeg", "jpg")
      return new File([file], `capture-${stamp}${files.length > 1 ? `-${i + 1}` : ""}.${ext}`, { type: file.type })
    }))
  }

  add(fileList) {
    for (const file of fileList) {
      if (this.files.items.length >= MAX_FILES) { alert(`${MAX_FILES} fichiers maximum.`); break }
      if (file.size > MAX_SIZE) { alert(`« ${file.name} » dépasse 15 Mo.`); continue }
      this.files.items.add(file)
    }
    this.sync()
  }

  remove(event) {
    event.preventDefault()
    const index = Number(event.currentTarget.dataset.index)
    const next = new DataTransfer()
    Array.from(this.files.files).forEach((f, i) => { if (i !== index) next.items.add(f) })
    this.files = next
    this.sync()
  }

  sync() {
    this.inputTarget.files = this.files.files
    this.previewsTarget.querySelectorAll("img").forEach((img) => URL.revokeObjectURL(img.src))
    this.previewsTarget.innerHTML = ""
    Array.from(this.files.files).forEach((file, index) => {
      const item = document.createElement("div")
      item.className = "dropzone__preview"
      if (file.type.startsWith("image/")) {
        const img = document.createElement("img")
        img.src = URL.createObjectURL(file)
        img.alt = file.name
        item.append(img)
      } else {
        const box = document.createElement("div")
        box.className = "file"
        box.innerHTML = `<i class="fa-solid fa-file-lines"></i>`
        box.append(document.createTextNode(file.name))
        item.append(box)
      }
      const button = document.createElement("button")
      button.type = "button"
      button.setAttribute("aria-label", `Retirer ${file.name}`)
      button.dataset.index = index
      button.dataset.action = "dropzone#remove"
      button.innerHTML = `<i class="fa-solid fa-xmark"></i>`
      item.append(button)
      this.previewsTarget.append(item)
    })
  }
}
